import * as Sentry from '@sentry/react';
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
// Initialize Sentry
if (SENTRY_DSN && SENTRY_DSN !== '') {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      import.meta.env.VITE_WEBSITE_DOMAIN,
      import.meta.env.VITE_API_DOMAIN,
      'localhost',
    ],
    // Session Replay
    replaysSessionSampleRate: 0.5, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}
import './App.css';
import React, { useState } from 'react';
// Add SuperTokens imports
import SuperTokens, { SuperTokensWrapper } from 'supertokens-auth-react';
import ThirdParty, { Google } from 'supertokens-auth-react/recipe/thirdparty';
import Session, { SessionAuth } from 'supertokens-auth-react/recipe/session';
// Add React Router imports
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { getSuperTokensRoutesForReactRouterDom } from 'supertokens-auth-react/ui';
import { ThirdPartyPreBuiltUI } from 'supertokens-auth-react/recipe/thirdparty/prebuiltui';
import * as reactRouterDom from 'react-router-dom';
import Dashboard from '@/app/dashboard/Routes';
import { settings } from '@/lib/settings';
import { client, listOrganisations } from '@/client/sdk.gen';
import type { OrganisationResponse, UserResponse } from '@/client/types.gen';
import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from '@/components/theme-provider';
import ErrorBoundary from '@/components/error-boundary';
import { Logo } from '@/components/ui/logo';
import { signOut } from 'supertokens-auth-react/recipe/session';
import { Loader2 } from 'lucide-react';
import {
  isPostHogEnabled,
  posthogKey,
  posthogHost,
  resetUser as resetPostHogUser,
} from '@/utils/featureFlags';
import { PostHogProvider } from 'posthog-js/react';
import { SELECTED_ORG_KEY } from '@/constants';
import BookingPage from '@/app/booking/page';
import ProtectedRescheduleBookingPage from '@/components/reschedule-booking-page';
import { IntercomProvider } from '@/components/intercom-provider';
import { resetUser as resetMixpanelUser, identifyUser } from '@/utils/mixpanel';
import { usePageTracking } from '@/hooks/usePageTracking';
import { setUser as setGAUser } from '@/utils/analytics';
import PatientOnboardPage from '@/app/patient/onboard/page';
import QuestionnairePage from '@/app/booking/questionnaire/page';
const PostHogWrapper = ({ children }: { children: React.ReactNode }) => {
  if (!isPostHogEnabled()) {
    return <>{children}</>;
  }
  return (
    <PostHogProvider
      apiKey={posthogKey}
      options={{
        api_host: posthogHost,
      }}
    >
      {children}
    </PostHogProvider>
  );
};

// Configure API client
client.setConfig({
  baseUrl: settings.apiDomain,
  credentials: 'include',
});

// Add request headers interceptor
client.interceptors.request.use((request) => {
  request.headers.set('Content-Type', 'application/json');
  request.headers.set('Accept', '*/*');
  return request;
});

// Add global error handling interceptor
// TODO: Define a proper type for the response from the client library
// eslint-disable-next-line @typescript-eslint/no-explicit-any
client.interceptors.response.use((response: any) => {
  if (!response.ok) {
    // Don't trigger signOut for auth-related endpoints
    const isAuthEndpoint = response.request.url.includes('/auth/');
    if (
      (response.status === 403 || response.status === 401) &&
      !isAuthEndpoint
    ) {
      resetPostHogUser();
      resetMixpanelUser();
      signOut();
      window.location.href = '/';
    }
  }
  return response;
});

interface OrganisationContext {
  organisationId: string;
  organisations: OrganisationResponse[];
  switchOrganisation: (orgId: string) => Promise<void>;
  loading: boolean;
}

// Create organization context
const OrganisationContext = React.createContext<OrganisationContext | null>(
  null
);

// Create organization provider component
function OrganisationProvider({ children }: { children: React.ReactNode }) {
  const [organisation, setOrganisation] =
    React.useState<OrganisationContext | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const navigate = useNavigate();

  const switchOrganisation = React.useCallback(async (orgId: string) => {
    try {
      setLoading(true);
      // Store the selected org ID
      localStorage.setItem(SELECTED_ORG_KEY, orgId);

      // Update client headers with new org ID
      client.setConfig({
        ...client.getConfig(),
        headers: {
          ...client.getConfig().headers,
          'X-Organisation-ID': orgId,
        },
      });

      // Update organization in state
      setOrganisation((prev) =>
        prev
          ? {
              ...prev,
              organisationId: orgId,
            }
          : null
      );

      // Refresh the page to reload all data with new org context
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch organization:', error);
      if (error instanceof Error) {
        setError(error);
        Sentry.captureException(error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const initializeOrganisation = React.useCallback(async () => {
    try {
      setLoading(true);
      const orgsResponse = await listOrganisations();
      if (!orgsResponse.data || orgsResponse.error) {
        throw new Error('Failed to fetch organizations');
      }

      const organisations = orgsResponse.data.organisations;

      // Get the stored org ID
      const storedOrgId = localStorage.getItem(SELECTED_ORG_KEY);

      // Find the stored org if it exists and is still valid
      let selectedOrg = storedOrgId
        ? organisations.find((org) => org.id === storedOrgId)
        : null;

      // Fall back to default org if stored org not found
      if (!selectedOrg) {
        selectedOrg = organisations.find((org) => org.is_default);
        if (selectedOrg) {
          localStorage.setItem(SELECTED_ORG_KEY, selectedOrg.id);
        }
      }

      if (!selectedOrg) {
        throw new Error('No valid organization found');
      }

      // Set organization in state and update client headers
      const orgId = selectedOrg.id;
      setOrganisation({
        organisationId: orgId,
        organisations,
        switchOrganisation,
        loading: false,
      });

      // Update client headers
      client.setConfig({
        ...client.getConfig(),
        headers: {
          ...client.getConfig().headers,
          'X-Organisation-ID': orgId,
        },
      });
    } catch (error) {
      console.error('Failed to initialize organization context:', error);
      if (error instanceof Error) {
        setError(error);
        Sentry.captureException(error);
      }
      // Redirect to login on organization fetch error
      navigate('/', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate, switchOrganisation]);

  React.useEffect(() => {
    initializeOrganisation();
  }, [initializeOrganisation]);

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Logo size="lg" />
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Logo size="lg" />
          <div className="text-center">
            <h1 className="text-xl font-semibold text-red-500">Error</h1>
            <p className="text-sm text-muted-foreground">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <OrganisationContext.Provider value={organisation}>
      {children}
    </OrganisationContext.Provider>
  );
}

// Create a hook to use organization context
export function useOrganisation() {
  const context = React.useContext(OrganisationContext);
  if (!context) {
    throw new Error(
      'useOrganisation must be used within an OrganisationProvider'
    );
  }
  return context;
}

// Initialize SuperTokens
SuperTokens.init({
  appInfo: {
    appName: 'Bleep',
    apiDomain: settings.apiDomain,
    websiteDomain: settings.websiteDomain,
    apiBasePath: '/auth',
    websiteBasePath: '',
  },
  style: `
    [data-supertokens~=superTokensBranding] {
      display: none;
    }
  `,
  getRedirectionURL: async (context) => {
    if (context.action === 'SUCCESS') {
      if (context.isNewRecipeUser) {
        // For new users, redirect to email verification
        return '/dashboard';
      }
      if (context.redirectToPath !== undefined) {
        return context.redirectToPath;
      }
      return '/dashboard';
    }
    return undefined;
  },
  recipeList: [
    ThirdParty.init({
      signInAndUpFeature: {
        providers: [Google.init()],
      },
      onHandleEvent: async (context) => {
        if (context.action === 'SUCCESS') {
          if (context.isNewRecipeUser) {
            // For new users, let the email verification flow handle it
            return;
          }

          // For existing users, initialize organization
          try {
            const orgsResponse = await listOrganisations();
            if (!orgsResponse.data || orgsResponse.error) {
              throw new Error('Failed to fetch organizations');
            }
            const defaultOrg = orgsResponse.data.organisations.find(
              (org) => org.is_default
            );
            if (!defaultOrg) {
              throw new Error('No default organization found');
            }

            // Update client headers with org ID
            client.setConfig({
              ...client.getConfig(),
              headers: {
                ...client.getConfig().headers,
                'X-Organisation-ID': defaultOrg.id,
              },
            });
          } catch (error) {
            console.error('Failed to initialize organization:', error);
          }
        }
      },
    }),
    Session.init(),
  ],
  termsOfServiceLink: 'https://gcal.ai/terms',
  privacyPolicyLink: 'https://gcal.ai/privacy',
});

// Create a Sentry-wrapped version of the AppContent component
const SentryAppContent = Sentry.withProfiler(AppContent);

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthPage = !location.pathname.startsWith('/dashboard');
  const [user, setUser] = useState<UserResponse | null>(null);

  // Add page tracking
  usePageTracking();

  React.useEffect(() => {
    // Only try to get user info if we're on an authenticated page
    if (!isAuthPage) {
      const fetchUser = async () => {
        try {
          const userResponse = await client.get<UserResponse>({
            url: '/v1/user/whoami',
          });

          if (userResponse.data) {
            setUser(userResponse.data);
            // Track user in Mixpanel
            identifyUser(
              userResponse.data.user_id,
              userResponse.data.email,
              userResponse.data.organisation_id,
              userResponse.data.name
            );
            // Track user in Google Analytics
            setGAUser(userResponse.data.user_id);
          }
        } catch (error) {
          console.error('Failed to fetch user data:', error);
        }
      };

      fetchUser();
    }
  }, [isAuthPage]);

  return (
    <div className="bg-background text-foreground">
      {isAuthPage && (
        <div className="flex justify-center p-4">
          <Logo size="lg" />
        </div>
      )}
      <IntercomProvider user={user}>
        <Routes>
          {/* SuperTokens Authentication Routes */}
          {getSuperTokensRoutesForReactRouterDom(reactRouterDom, [
            ThirdPartyPreBuiltUI,
          ])}

          {/* Public Booking Route */}
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/patient/onboard" element={<PatientOnboardPage />} />
          <Route
            path="/booking/questionnaire"
            element={<QuestionnairePage />}
          />

          {/* Username/Event-Slug Route */}
          <Route path="/:username/:eventTypeSlug" element={<BookingPage />} />

          {/* Protected Reschedule Booking Route */}
          <Route
            path="/reschedule/:bookingId"
            element={<ProtectedRescheduleBookingPage />}
          />

          {/* Dashboard Routes */}
          <Route
            path="/dashboard/*"
            element={
              <SessionAuth
                onSessionExpired={() => {
                  // Redirect to home page on session expiry
                  navigate('/', { replace: true });
                }}
              >
                <OrganisationProvider>
                  <ErrorBoundary>
                    <Dashboard />
                  </ErrorBoundary>
                </OrganisationProvider>
              </SessionAuth>
            }
          />
        </Routes>
      </IntercomProvider>
    </div>
  );
}

function App() {
  return (
    <PostHogWrapper>
      <ThemeProvider>
        <SuperTokensWrapper>
          <BrowserRouter>
            <ErrorBoundary>
              <SentryAppContent />
            </ErrorBoundary>
            <Toaster />
          </BrowserRouter>
        </SuperTokensWrapper>
      </ThemeProvider>
    </PostHogWrapper>
  );
}

export default App;
